'use client';

import { FC, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { useClientLocale, useClientTranslation } from '@bts-web/utils-lokalise';
import { getAppConfig } from '@bts-web/core-app-config';
import {
  intlAssetValueFormatting,
  MaximumFractionDigits,
} from '@bts-web/utils-formatting';
import { convertToLocaleDateString } from '@bts-web/utils-formatting-client';
import {
  AmountInputWrapper,
  BelowInputInfoTable,
  ButtonBase,
} from '../../../common';
import { datadogErrorHelper } from '../../../common/utils/datadogErrorHelper';
import { useHandleGqlErrorsWithSnackbar } from '../../../common/utils/errors';
import { createStakeAction } from '../../../common/gqlActions';
import { TradeAssetInfoDisplay } from '../../../common/components/TradeAssetInfoDisplay/TradeAssetInfoDisplay';
import { FilledSpinnerIos } from '../../../common/svgIcons';
import { PoweredByTextWithLogo } from '../../../trade/Reusable/PoweredBy/PoweredBy';
import { StakeTranslations } from '../../utils/getStakeTranslations';
import { PerformanceDisclaimerBanner } from '../../../performance-disclaimers/components/PerformanceDisclaimerBanner.use-client';
import { EstimatedEarningsInfoIcon } from '../../../performance-disclaimers/components/EstimatedEarningsInfoIcon.use-client';
import { getPerformanceDisclaimerClientTranslations } from '../../../performance-disclaimers/utils/getClientTranslations';
import {
  finishTradeRedirectBuilder,
  IFinishTradeRedirectBuilderProps,
} from '../../../trade/FinishTrade/actions/finishTradeRedirectBuilder.action';
import { StakingRewardViewQuery } from '@bts-web/data-layer/server';
import {
  ConversionSettings,
  getConvertedSummary,
} from '../../../trade/CreateTrade/utils/getConvertedSummary';
import { isTooHighOrToLowAmount } from '../../../trade/CreateTrade/MainTradeForm/hooks/useTradeInput';
import {
  getMaximumTradeAmount,
  getMinimumTradeAmount,
} from '../../../trade/CreateTrade/utils';
import { MainTradeFormError } from '../../../trade/CreateTrade/MainTradeForm/subcomponents/MainTradeFormError';
import { TradeAssetDetails } from '../../../trade/CreateTrade/MainTradeForm/types';
import { MutableNonNullableAsset } from '../../../trade/CreateTrade/types';
import { AssetType } from '../../../asset-details/types';

const executeRedirect = (props: IFinishTradeRedirectBuilderProps) => {
  finishTradeRedirectBuilder(props);
};

export type AcceptStakeSummaryProps = {
  translations: StakeTranslations;
  assetId: string;
  amount: string;
  stakingReward: NonNullable<
    StakingRewardViewQuery['response']['stakingReward']
  >;
};

export const AcceptStakeSummary: FC<AcceptStakeSummaryProps> = ({
  translations,
  assetId,
  stakingReward,
  amount,
}) => {
  const { t } = useClientTranslation();

  const disclaimerTranslations = getPerformanceDisclaimerClientTranslations(t);

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const locale = useClientLocale();

  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const [isConfirmDone, setIsConfirmDone] = useState(false);

  const { asset, assetReward, nextRewardAt, __typename } = stakingReward;

  const { logoUrl, name, price, portfolio, symbol, staking } = asset;

  const {
    rewardIntervalInDays,
    type,
    unboundedPeriodInDays,
    yearlyInterest,
    minimumStakeableAmount,
  } = staking || {};

  const maximumTradeAmount = getMaximumTradeAmount({
    assetBalance: portfolio?.assetBalance,
    fiatBalance: portfolio?.fiatBalance,
    type: 'stake',
  });

  const minimumTradeAmount = getMinimumTradeAmount({
    minimumBuyAmount: undefined,
    minimumSellAmount: minimumStakeableAmount,
    price,
    type: 'stake',
  });

  const cleanedNumberDecimalsPreserved = Number(amount.replace(/[^0-9]/g, '.'));

  const currentTradeAmount = Number(
    cleanedNumberDecimalsPreserved.toFixed(MaximumFractionDigits.ASSET),
  );

  const { isTooHigh, isTooLow } = isTooHighOrToLowAmount({
    maximumTradeAmount: maximumTradeAmount['ASSET'],
    minimumTradeAmount: minimumTradeAmount['ASSET'],
    currentTradeAmount: currentTradeAmount,
  });

  const hasAnyErrors = isTooHigh || isTooLow;

  const { currency } = getAppConfig();

  const triggerAcceptStake = async () => {
    setIsConfirmLoading(true);

    try {
      const response = await createStakeAction({
        assetId,
        amount,
      });

      if (response.errors) {
        setIsConfirmLoading(false);

        setIsConfirmDone(false);

        executeRedirect({
          assetId,
          transactionStatus: 'FAILED',
          offerId: 'accept_stake', // in case of staking fails we redirecting to /accept/FAILED we do not have stakeId
          variant: 'stake',
        });

        return;
      }

      if (!response.data || !response.data.stake) {
        throw new Error('server failure: missing response data');
      }

      executeRedirect({
        assetId,
        transactionStatus: 'SUCCESS',
        offerId: response.data.stake.id,
        variant: 'stake',
      });
    } catch (error) {
      setIsConfirmLoading(false);

      setIsConfirmDone(false);

      processErrors(
        typeof error === 'string'
          ? [error]
          : typeof error === 'object' &&
              error &&
              'errors' in error &&
              Array.isArray((error as any).errors)
            ? (error as any).errors
            : [],
      );

      datadogErrorHelper({
        errorMessage:
          error instanceof Error ? error.message : JSON.stringify(error),
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });
    }
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        px: 'medium',
        height: '100%',
        gap: 'medium',
      })}
    >
      <div>
        <TradeAssetInfoDisplay
          sourceAssetData={
            { logoUrl, name, __typename } as MutableNonNullableAsset
          }
          symbol={symbol as string}
          operation="stake"
        />

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'medium',
          })}
        >
          <AmountInputWrapper
            inputContextData={{
              symbol: symbol as string,
              currency,
              displayType: 'ASSET',
              value: amount,
              placeholder: undefined,
            }}
          />

          <BelowInputInfoTable
            items={[
              {
                value: getConvertedSummary({
                  assetPrice: price,
                  tradeType: 'stake',
                  assetType: AssetType.CRYPTO,
                  currency,
                  displayType: 'ASSET',
                  locale,
                  symbol: symbol as string,
                  translations: {
                    afterFees: translations.afterFees,
                  } as ConversionSettings['translations'],
                  value: amount,
                }),
              },
            ]}
          />

          <MainTradeFormError
            locale={locale}
            transactionType="stake"
            errors={{
              tooHigh: isTooHigh,
              tooLow: isTooLow,
              customError: '',
            }}
            minimumTradeAmount={minimumTradeAmount}
            assetDetails={
              {
                id: assetId,
                logoUrl,
                name,
                symbol,
              } as TradeAssetDetails
            }
          />

          {type === 'LOCKED' && (
            <div
              className={css({
                borderWidth: '1px',
                borderColor: 'information.stroke_primary',
                color: 'information.text_primary',
                padding: 'small',
                borderEndEndRadius: 'smallBottomRight',
                borderEndStartRadius: 'smallTopRight',
                borderStartStartRadius: 'smallTopLeft',
                borderStartEndRadius: 'smallBottomLeft',
              })}
            >
              {t('bonded_stake_warning', {
                days: unboundedPeriodInDays,
              })}
            </div>
          )}

          <div
            className={css({
              pt: 'extra_small',
              borderColor: 'neutrals.divider',
              borderTopWidth: '1px',
            })}
          >
            <BelowInputInfoTable
              items={[
                {
                  title: (
                    <span
                      className={css({
                        color: 'neutrals.text_secondary',
                        fontSize: 'body.medium',
                        fontWeight: 'body.medium',
                        letterSpacing: 'body.medium',
                        lineHeight: 'body.medium',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'extra_small_2',
                      })}
                    >
                      {translations.estimatedRewards}
                      <EstimatedEarningsInfoIcon
                        translations={disclaimerTranslations}
                      />
                    </span>
                  ),
                  value: (
                    <span
                      className={css({
                        color: 'positive.text_primary',
                        fontFamily: 'label.medium_bold',
                        fontSize: 'label.medium_bold',
                        fontWeight: 'label.medium_bold',
                        lineHeight: 'label.medium_bold',
                        letterSpacing: 'label.medium_bold',
                      })}
                    >
                      {t('annual_interest_rate', {
                        value: `${yearlyInterest}%`,
                      })}
                    </span>
                  ),
                },
                {
                  title: translations.firstRewardPayout,
                  value: convertToLocaleDateString(nextRewardAt, locale),
                },
                {
                  title: translations.expectedNextReward,
                  value: assetReward?.value
                    ? `≈ ${intlAssetValueFormatting(assetReward.value, {
                        fractionDigits: assetReward.precision,
                      })} ${symbol}`
                    : '',
                },
                {
                  title: translations.rewardsPayoutInterval,
                  value:
                    rewardIntervalInDays === 7
                      ? translations.weekly
                      : translations.monthly,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <PerformanceDisclaimerBanner
        translations={disclaimerTranslations}
        variant="fixed"
        className={css({ padding: 0 })}
      />

      <div
        className={css({
          pb: 'medium',
          marginTop: 'auto',
        })}
      >
        <PoweredByTextWithLogo />

        <ButtonBase
          data-testid="stake-confirm-button"
          visual="primary"
          size="large"
          disabled={isConfirmLoading || isConfirmDone || hasAnyErrors}
          onClick={triggerAcceptStake}
          fullWidth
        >
          {translations.stakeNow}

          {isConfirmLoading && (
            <FilledSpinnerIos
              aria-label="loading icon"
              data-element="loading-icon"
            />
          )}
        </ButtonBase>
      </div>
    </div>
  );
};
